import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildEntityIndex,
  resolveRef,
  normalizeKindId,
  type Adventure,
  type EntityIndex,
  type IndexedEntity,
  type Section,
} from '@gm-binder/shared';

/** Legacy `section.type` → canonical kind id, for sections authored before `kind` existed. */
const TYPE_TO_KIND: Record<string, string> = {
  rooms: 'place',
  characters: 'character',
  npcs: 'npc',
  bestiary: 'beast',
  encounters: 'encounter',
  initiative: 'initiative',
  clues: 'clue',
  items: 'item',
  curses: 'table',
  notes: 'notes',
  gallery: 'gallery',
  overview: 'overview',
  page: 'page',
};

/** Section types that expose a dedicated detail route (`/:sectionId/:entityId`). */
const DETAIL_TYPES = new Set(['rooms', 'characters']);

function sectionKind(section: Section): string {
  if (section.kind) return normalizeKindId(section.kind);
  return TYPE_TO_KIND[section.type] ?? section.type;
}

export interface RefNavValue {
  index: EntityIndex;
  /** Resolve a reference (namespaced, or bare id/name with `fromKind`) to an entity. */
  resolve: (ref: string, fromKind?: string) => IndexedEntity | undefined;
  /** Compute the in-app route for a reference, or null when it can't be resolved/placed. */
  hrefFor: (ref: string, fromKind?: string) => string | null;
  /** Navigate to the referenced entity (no-op when unresolvable). */
  navigateRef: (ref: string, fromKind?: string) => void;
}

const RefNavContext = createContext<RefNavValue | null>(null);

/**
 * Provides entity-reference resolution + navigation to the running binder. Wrap the binder
 * content so descendants (MarkdownView wiki-links, RefPicker, Backlinks) can resolve and
 * jump between entities. Maps a target kind to its section route, using a detail route for
 * places/characters and `?focus=<id>` otherwise.
 */
export function RefNavProvider({
  adventure,
  instanceId,
  children,
}: {
  adventure: Adventure;
  instanceId: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();

  const value = useMemo<RefNavValue>(() => {
    const index = buildEntityIndex(adventure);

    const sectionForKind = (kind: string): Section | undefined => {
      const k = normalizeKindId(kind);
      return adventure.sections.find((s) => s.enabled !== false && sectionKind(s) === k);
    };

    const hrefForEntity = (target: IndexedEntity): string | null => {
      const section = sectionForKind(target.kind);
      if (!section) return null;
      const base = `/run/${instanceId}/${section.id}`;
      if (DETAIL_TYPES.has(section.type)) {
        // Character detail routes key on the (unique) name; rooms key on id.
        const param =
          section.type === 'characters' ? String(target.entity.name ?? target.id) : target.id;
        return `${base}/${encodeURIComponent(param)}`;
      }
      return `${base}?focus=${encodeURIComponent(target.id)}`;
    };

    const resolve = (ref: string, fromKind?: string) => resolveRef(index, ref, fromKind);

    const hrefFor = (ref: string, fromKind?: string) => {
      const target = resolve(ref, fromKind);
      return target ? hrefForEntity(target) : null;
    };

    const navigateRef = (ref: string, fromKind?: string) => {
      const href = hrefFor(ref, fromKind);
      if (href) navigate(href);
    };

    return { index, resolve, hrefFor, navigateRef };
  }, [adventure, instanceId, navigate]);

  return <RefNavContext.Provider value={value}>{children}</RefNavContext.Provider>;
}

/** Ref-navigation context, or null when rendered outside a running binder. */
export function useRefNav(): RefNavValue | null {
  return useContext(RefNavContext);
}
